import {compact, trim} from 'lodash';

import {
  addQueryToPath,
  parseQueryString,
  tryParseUrl,
} from '../../libs/shared/core/src/helpers/browser/urlHelpers';
import {CleanHtml, emptyCleanHtml} from '../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {Dictionary} from '../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {isHtml} from '../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {RecipientRolesEnum} from '../../libs/shared/core/src/models/api/recipientModel';
import {emailRecipientParser, phoneRecipientParser} from '../../src/helpers/models/recipientParsers';
import {VisualRecipientModel} from '../../src/models/api/visualRecipientModel';
import {htmlToText} from '../services/converters/htmlToTextConverter';
import {textToHtml} from '../services/converters/textToHtmlConverter';

export const supportedProtocols = ['mailto:', 'mailto-frontapp:', 'frontapp-mailto:'];

export interface MailtoOptions {
  from: string;
  recipients: ReadonlyArray<VisualRecipientModel>;
  subject: string;
  trustedHtml: CleanHtml;
  text: string;
}

export function buildMailto(options: {recipients: ReadonlyArray<string>; subject?: string; body?: string}) {
  const {recipients, body, subject} = options;
  return addQueryToPath(`mailto:${recipients.join(';')}`, {
    subject,
    body,
  });
}

export function parseMailtoSanitized(
  mailto: string,
  sanitizer: (dirtyHtml: string) => CleanHtml,
): MailtoOptions | undefined {
  const url = tryParseMailtoUrl(mailto);
  if (!url || !supportedProtocols.includes(url.protocol)) {
    return undefined;
  }

  // Parse mailto qs.
  const qs = parseQueryString(url.search);

  // Extract recipients.
  const to = parseRecipients(RecipientRolesEnum.TO, url.pathname);
  const cc = parseRecipients(RecipientRolesEnum.CC, qs.cc || '');
  const bcc = parseRecipients(RecipientRolesEnum.BCC, qs.bcc || '');

  // Message options.
  return {
    from: cleanString(qs.from || ''),
    recipients: [...to, ...cc, ...bcc],
    subject: cleanString(qs.subject || ''),
    trustedHtml: parseTrustedHtml(qs, sanitizer),
    text: parseText(qs),
  };
}

/** Tries to parse the url, if the url has params but we did not get any parsed try to fix the url */
function tryParseMailtoUrl(mailto: string) {
  const url = tryParseUrl(mailto);
  // Try replacing the first & we find and try parsing again
  if (mailto.includes('&') && !url?.search) {
    return tryParseUrl(mailto.replace('&', '?'));
  }
  return url;
}

function parseRecipients(role: RecipientRolesEnum, text: string) {
  if (!text) {
    return [];
  }

  const recipientsOrNull = text.split(/,|;/g).map((rawRecipient) => {
    if (!rawRecipient) {
      return null;
    }

    const decoded = decodeURIComponent(rawRecipient).trim();
    return emailRecipientParser(role, decoded) || phoneRecipientParser(role, decoded);
  });

  return compact(recipientsOrNull);
}

function cleanString(text: string) {
  return trim(text, `"'`);
}

function parseTrustedHtml(
  {body, html, text}: Dictionary<string | undefined>,
  sanitizer: (dirtyHtml: string) => CleanHtml,
): CleanHtml {
  const isBodyHtml = body && isHtml(body);

  // HTML body.
  if (body && isBodyHtml) {
    return sanitizer(body);
  }

  // HTML.
  if (html) {
    return sanitizer(html);
  }

  // Text body -> HTML.
  if (body && !isBodyHtml) {
    return textToHtml(body);
  }

  // Text -> HTML.
  if (text) {
    return textToHtml(text);
  }

  return emptyCleanHtml;
}

function parseText({body, html, text}: Dictionary<string | undefined>) {
  const isBodyHtml = body && isHtml(body);

  // Text body.
  if (body && !isBodyHtml) {
    return body;
  }

  // Text.
  if (text) {
    return text;
  }

  // HTML body -> Text.
  if (body && isBodyHtml) {
    return htmlToText(body);
  }

  // HTML -> Text.
  if (html) {
    return htmlToText(html);
  }

  return '';
}
